import { useTranslation } from 'react-i18next';
import type { FlatfinderEstate, FlatfinderRegion } from 'realitiq-utils/src/flatfinder';
import { Button } from '@/components/ui/button';
import { FlatfinderApartmentList } from './apartment-list';

export function FlatfinderApartmentPanel({
  assignmentVersion,
  busy,
  canAssign,
  canCreate,
  canCreateForSelection,
  canConnectSelection,
  estates,
  highlightedEstateId,
  panelVisible,
  perspectiveId,
  regions,
  selectedRegionId,
  onClose,
  onConnect,
  onCreate,
  onDisconnect,
  onEdit,
  onHover,
  onSelect,
  onStartConnect,
}: {
  assignmentVersion: number;
  busy: boolean;
  canAssign: boolean;
  canCreate: boolean;
  canCreateForSelection: boolean;
  canConnectSelection: boolean;
  estates: FlatfinderEstate[];
  highlightedEstateId: string | null;
  panelVisible: boolean;
  perspectiveId?: string;
  regions: FlatfinderRegion[];
  selectedRegionId: string | null;
  onClose: () => void;
  onConnect: (estateId: string | null) => void;
  onCreate: () => void;
  onDisconnect: (estateId: string) => void;
  onEdit: (estateId: string) => void;
  onHover: (estateId: string | null) => void;
  onSelect: (regionId: string | null) => void;
  onStartConnect: (estateId: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <aside
      aria-label={t('apartments')}
      className={`${panelVisible ? 'flex' : 'hidden'} flex-col gap-3 overflow-y-auto rounded-lg border bg-white p-4 max-lg:fixed max-lg:inset-x-2 max-lg:bottom-2 max-lg:z-40 max-lg:max-h-[75dvh] max-lg:shadow-2xl lg:flex lg:max-h-[80dvh]`}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('apartments')}</h2>
        <Button type="button" variant="outline" className="lg:hidden" onClick={onClose}>
          {t('close')}
        </Button>
      </div>
      <FlatfinderApartmentList
        key={`${perspectiveId ?? 'empty'}-${assignmentVersion}`}
        estates={estates}
        regions={regions}
        busy={busy}
        canAssign={canAssign}
        selectedId={selectedRegionId}
        canConnect={canConnectSelection}
        onHover={onHover}
        hoveredEstateId={highlightedEstateId}
        onEdit={onEdit}
        onSelect={onSelect}
        onConnect={onConnect}
        onStartConnect={onStartConnect}
        onDisconnect={onDisconnect}
      />
      {canCreateForSelection ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-text-secondary">{t('flatfinderWorkspaceConnectSelected')}</p>
          <Button disabled={busy || !canCreate} onClick={onCreate}>
            {t('createApartment')}
          </Button>
        </div>
      ) : null}
      {estates.length === 0 ? (
        <p className="text-sm text-text-secondary">{t('flatfinderWorkspaceEmpty')}</p>
      ) : null}
    </aside>
  );
}
